/**
 * StaffOrderPage — order Eduvos-branded staff apparel against a personal
 * voucher.
 *
 * No role guard: the API resolves the caller from the verified sign-in and
 * returns only their own voucher and order. Built phone-first, because most
 * people arrive from the invitation email: on a narrow screen the order sits
 * in a summary bar at the bottom that opens into a sheet; on a wide screen it
 * is a panel beside the catalogue.
 *
 * The order is put together on the page and saved whole (PUT replaces it).
 * Lines from a saved order keep the price they were saved at; the server has
 * the last word on every save, and its `detail` wording is shown as-is.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { CheckCircle2, ExternalLink, Loader2, RefreshCw, ShoppingBag } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import {
  addLine,
  errorDetail,
  formatRand,
  formatSast,
  itemCount,
  linesFromOrder,
  orderTotal,
  piecesLabel,
  remainingVoucher,
  removeLine,
  repriceLines,
  safeLink,
  sameLines,
  setLineQuantity,
  sizePrice,
  type DraftLine,
} from '@/lib/staffOrder'
import {
  staffOrderService,
  type StaffOrderAllowance,
  type StaffOrderCurrent,
  type StaffOrderItem,
  type StaffOrderRound,
} from '@/services/api/staffOrderService'

import { CatalogueCard } from './CatalogueCard'
import { OrderPanel, ReadOnlyOrder, type OrderPanelProps } from './OrderPanel'
import { useMediaQuery } from './useMediaQuery'

export const NO_ORDER_OPEN =
  'There is no staff order open for you right now. If you think you should have a voucher, please contact Finance.'

type LineKey = Pick<DraftLine, 'item_id' | 'size'>

function PageShell({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('mx-auto max-w-6xl space-y-5 py-4 sm:py-6', className)}>{children}</div>
}

function PageHeader({ round, allowance }: { round: StaffOrderRound; allowance: StaffOrderAllowance }) {
  // Once ordering has closed, "Order by" reads as a deadline still to meet; the reason box says when it closed.
  const closes = round.status === 'closed' ? '' : formatSast(round.closes_at)
  return (
    <header className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="flex items-center gap-2 font-serif text-2xl text-heritage-blue-700">
          <ShoppingBag className="h-6 w-6 shrink-0" />
          {round.name}
        </h1>
        {allowance.is_test && (
          <Badge className="border-amber-200 bg-amber-100 text-amber-800 hover:bg-amber-100">Test</Badge>
        )}
      </div>
      <div className="flex flex-col gap-1 text-sm text-gray-700 sm:flex-row sm:flex-wrap sm:gap-x-6">
        <p>
          Your voucher: <span className="font-semibold text-gray-900">{formatRand(allowance.amount_cents)}</span>
          {allowance.tier_label && (
            <span className="ml-2 text-xs text-gray-400">{allowance.tier_label}</span>
          )}
        </p>
        {closes && (
          <p>
            Order by <span className="font-semibold text-gray-900">{closes}</span>
          </p>
        )}
      </div>
    </header>
  )
}

function MobileOrderBar({
  count,
  totalCents,
  leftCents,
  placed,
  unsaved,
  onOpen,
}: {
  count: number
  totalCents: number
  leftCents: number
  placed: boolean
  unsaved: boolean
  onOpen: () => void
}) {
  let status: ReactNode
  if (placed) {
    status = (
      <span className="inline-flex items-center gap-1 text-emerald-700">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Your order is in
      </span>
    )
  } else if (unsaved) {
    status = <span className="text-amber-700">Changes not saved yet</span>
  } else if (leftCents < 0) {
    status = <span className="text-red-600">Over your voucher by {formatRand(-leftCents)}</span>
  } else {
    status = `${formatRand(leftCents)} left on your voucher`
  }
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white/95 px-4 pt-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] backdrop-blur lg:hidden"
      style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
    >
      <div className="mx-auto flex max-w-xl items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-heritage-blue-700">
            {count === 0 ? 'Your order is empty' : `${piecesLabel(count)} · ${formatRand(totalCents)}`}
          </p>
          <p className="truncate text-xs text-gray-500">{status}</p>
        </div>
        <Button type="button" className="h-11 shrink-0 px-5" onClick={onOpen}>
          {placed ? 'View order' : 'Review order'}
        </Button>
      </div>
    </div>
  )
}

export default function StaffOrderPage() {
  const [current, setCurrent] = useState<StaffOrderCurrent | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [lines, setLines] = useState<DraftLine[]>([])
  const [campus, setCampus] = useState('')
  const [campusMissing, setCampusMissing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const isDesktop = useMediaQuery('(min-width: 1024px)')

  /** Take the server's view of the page, and start the page's lines from the saved order. */
  const adopt = useCallback((next: StaffOrderCurrent) => {
    setCurrent(next)
    setLines(linesFromOrder(next.order))
    setCampus(next.order?.delivery_campus ?? '')
    setCampusMissing(false)
  }, [])

  const load = useCallback(() => {
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    staffOrderService
      .getCurrent()
      .then((res) => {
        if (!cancelled) adopt(res)
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setLoadError(errorDetail(e, 'Your staff order could not be loaded right now. Please try again.'))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [adopt])

  useEffect(() => load(), [load])

  const round = current?.round ?? null
  const allowance = current?.allowance ?? null
  const order = current?.order ?? null
  const items = useMemo(() => current?.items ?? [], [current])
  // Items HR switched off come back only when they are in the person's order: shown there, never offered.
  const catalogue = useMemo(() => items.filter((i) => i.available !== false), [items])
  const campuses = useMemo(() => current?.campuses ?? [], [current])
  const itemsById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items])
  const savedLines = useMemo(() => linesFromOrder(order), [order])
  const voucher = allowance?.amount_cents ?? 0
  const canOrder = !!round?.can_order
  const dirty = order
    ? !sameLines(lines, savedLines) || campus !== order.delivery_campus
    : lines.length > 0
  // A save prices the whole order from today's catalogue; an untouched order
  // still shows the prices it was placed at.
  const linesNow = useMemo(() => repriceLines(lines, itemsById), [lines, itemsById])
  const shownLines = dirty ? linesNow : lines
  const pricesChanged = dirty && !!order && linesNow.some((line, i) => line !== lines[i])
  const total = orderTotal(shownLines)

  // Closing the tab with an unsaved order loses it; say so first.
  useEffect(() => {
    if (!dirty || !canOrder) return
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty, canOrder])

  const clearMessages = () => {
    setNotice(null)
    setError(null)
  }

  const handleAdd = (item: StaffOrderItem, size: string, quantity: number) => {
    const price = sizePrice(item, size)
    if (price === null) return
    clearMessages()
    setLines((prev) => addLine(prev, { item_id: item.id, size, quantity, unit_price_cents: price }))
  }

  const handleQuantity = (key: LineKey, quantity: number) => {
    clearMessages()
    setLines((prev) => setLineQuantity(prev, key, quantity))
  }

  const handleRemove = (key: LineKey) => {
    clearMessages()
    setLines((prev) => removeLine(prev, key))
  }

  const handleUndo = () => {
    clearMessages()
    setLines(savedLines)
    setCampus(order?.delivery_campus ?? '')
    setCampusMissing(false)
  }

  const handleCampus = (code: string) => {
    clearMessages()
    setCampus(code)
    setCampusMissing(false)
  }

  const handleSave = async () => {
    if (!round || saving) return
    clearMessages()
    if (!campus) {
      setCampusMissing(true)
      return
    }
    setSaving(true)
    try {
      const next = await staffOrderService.saveMyOrder(round.id, {
        delivery_campus: campus,
        lines: lines.map(({ item_id, size, quantity }) => ({ item_id, size, quantity })),
      })
      adopt(next)
      const closes = formatSast(next.round?.closes_at ?? round.closes_at)
      setNotice(closes ? `Your order is in. You can change it until ${closes}.` : 'Your order is in.')
    } catch (e) {
      setError(errorDetail(e, 'Your order could not be saved. Please try again.'))
    } finally {
      setSaving(false)
    }
  }

  const handleCancelOrder = async () => {
    if (!round || cancelling) return
    clearMessages()
    setCancelling(true)
    try {
      const next = await staffOrderService.cancelMyOrder(round.id)
      adopt(next)
      const closes = formatSast(next.round?.closes_at ?? round.closes_at)
      setNotice(
        closes
          ? `Your order has been cancelled. You can still place a new order until ${closes}.`
          : 'Your order has been cancelled.',
      )
    } catch (e) {
      setError(errorDetail(e, 'Your order could not be cancelled. Please try again.'))
    } finally {
      setCancelling(false)
    }
  }

  // ---------------------------------------------------------------- states

  if (loading) {
    return (
      <PageShell>
        <div className="py-16 text-center text-sm text-gray-400">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          Loading your staff order…
        </div>
      </PageShell>
    )
  }

  if (loadError) {
    return (
      <PageShell className="max-w-2xl">
        <div className="space-y-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          <p>{loadError}</p>
          <Button type="button" variant="outline" size="sm" onClick={() => load()}>
            <RefreshCw className="h-4 w-4" />
            Try again
          </Button>
        </div>
      </PageShell>
    )
  }

  if (!round || !allowance) {
    return (
      <PageShell className="max-w-2xl">
        <h1 className="flex items-center gap-2 font-serif text-2xl text-heritage-blue-700">
          <ShoppingBag className="h-6 w-6" />
          Staff order
        </h1>
        <Card>
          <CardContent className="px-6 py-10 text-center">
            <ShoppingBag className="mx-auto mb-3 h-10 w-10 text-gray-300" />
            <p className="text-sm text-gray-600">{NO_ORDER_OPEN}</p>
          </CardContent>
        </Card>
      </PageShell>
    )
  }

  // ---------------------------------------------------- ordering closed

  if (!canOrder) {
    const reason =
      round.closed_reason ||
      (round.status === 'draft' ? 'Ordering has not opened yet.' : 'Ordering is closed.')
    return (
      <PageShell className="max-w-2xl">
        <PageHeader round={round} allowance={allowance} />
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" role="status">
          {reason}
        </div>
        <Card>
          <CardContent className="space-y-2 p-4 sm:p-5">
            <h2 className="text-lg font-semibold text-heritage-blue-700">Your order</h2>
            <ReadOnlyOrder
              order={order}
              itemsById={itemsById}
              voucherCents={voucher}
              campuses={campuses}
              roundClosed={round.status === 'closed'}
            />
          </CardContent>
        </Card>
      </PageShell>
    )
  }

  // -------------------------------------------------------- ordering open

  const guide = safeLink(round.size_guide_url)
  const panelProps: OrderPanelProps = {
    lines: shownLines,
    checkLines: linesNow,
    pricesChanged,
    itemsById,
    voucherCents: voucher,
    campuses,
    campus,
    onCampusChange: handleCampus,
    campusMissing,
    hasOrder: !!order,
    dirty,
    saving,
    cancelling,
    closesAt: round.closes_at,
    lastChanged: order?.updated_at ?? null,
    notice,
    error,
    onQuantity: handleQuantity,
    onRemove: handleRemove,
    onSave: handleSave,
    onCancelOrder: handleCancelOrder,
    onUndo: handleUndo,
  }

  return (
    <PageShell className={isDesktop ? undefined : 'pb-28'}>
      <PageHeader round={round} allowance={allowance} />

      {(round.intro_text || guide) && (
        <Card>
          <CardContent className="space-y-3 p-4 sm:p-5">
            {round.intro_text && (
              <p className="whitespace-pre-line text-sm leading-relaxed text-gray-700">{round.intro_text}</p>
            )}
            {guide && (
              <a
                href={guide}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm font-medium text-future-blue-700 hover:underline"
              >
                Size guide
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </CardContent>
        </Card>
      )}

      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start lg:gap-6">
        <section aria-label="Items you can order">
          {catalogue.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center text-sm text-gray-500">
                There are no items to order right now. Please check again later.
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4 min-[520px]:grid-cols-2 2xl:grid-cols-3">
              {catalogue.map((item) => (
                <CatalogueCard
                  key={item.id}
                  item={item}
                  lines={linesNow}
                  voucherCents={voucher}
                  onAdd={handleAdd}
                />
              ))}
            </div>
          )}
        </section>

        {isDesktop && (
          <aside className="sticky top-4 max-h-[calc(100vh-6rem)] overflow-y-auto rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <OrderPanel {...panelProps} />
          </aside>
        )}
      </div>

      {!isDesktop && (
        <>
          <MobileOrderBar
            count={itemCount(lines)}
            totalCents={total}
            leftCents={remainingVoucher(voucher, total)}
            placed={!!order && !dirty}
            unsaved={!!order && dirty}
            onOpen={() => setSheetOpen(true)}
          />
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto rounded-t-2xl px-4 pb-8 pt-5">
              <SheetHeader className="text-left">
                <SheetTitle className="text-heritage-blue-700">Your order</SheetTitle>
                <SheetDescription>Check your items and delivery campus.</SheetDescription>
              </SheetHeader>
              <div className="mt-4">
                <OrderPanel {...panelProps} hideHeading />
              </div>
            </SheetContent>
          </Sheet>
        </>
      )}
    </PageShell>
  )
}
